import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Hidden from '@material-ui/core/Hidden';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import PropTypes from 'prop-types';
import React from 'react';
import { Controller as FormController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import emailImg from '../images/email-capture.png';
import subscribeEmail from '../services/subscribe';
import DoneIcon from './icons/done';
import PercentageIcon from './icons/percentage';
import Loader from './loader';
import Notification from './notification';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'fixed',
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 100,
    width: '100%',
    padding: theme.spacing(6, 0),
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0px -1px 1px rgba(0, 0, 0, 0.08)',
    [theme.breakpoints.up('sm')]: {
      paddingRight: theme.spacing(16)
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4, 0)
    }
  },
  wrap: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      alignItems: 'center'
    }
  },
  header: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      alignItems: 'center',
      marginRight: theme.spacing(6)
    },
    [theme.breakpoints.up('lg')]: {
      marginRight: theme.spacing(12)
    }
  },
  headerIcon: {
    marginRight: theme.spacing(5),
    '& svg': {
      fontSize: 54
    }
  },
  icon: {
    display: 'block',
    width: 46,
    height: 32,
    backgroundImage: `url(${emailImg})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'cenyter'
  },
  formWrap: {
    position: 'relative',
    paddingTop: theme.spacing(6),
    [theme.breakpoints.up('md')]: {
      flex: '0 0 62%',
      width: '100%',
      paddingTop: 0
    }
  },
  form: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      alignItems: 'flex-end'
    }
  },
  input: {
    width: '100%',
    marginBottom: theme.spacing(11),
    [theme.breakpoints.up('md')]: {
      width: 256,
      marginRight: theme.spacing(6),
      marginBottom: 0
    },
    [theme.breakpoints.up('lg')]: {
      marginRight: theme.spacing(12)
    }
  },
  loader: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    zIndex: 2,
    transform: 'translate(-50%, -50%)'
  },
  cancelButton: {
    paddingRight: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    marginRight: theme.spacing(3),
    border: `1px solid ${theme.colors.borderColor}`,
    [theme.breakpoints.up('md')]: {
      order: 1,
      marginRight: 0,
      marginLeft: theme.spacing(6)
    }
  },
  gratitudeWrap: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(11, 4, 4),
    '& svg': {
      marginRight: theme.spacing(7)
    },
    [theme.breakpoints.up('md')]: {
      flex: '1 0',
      justifyContent: 'center',
      width: '100%',
      padding: 0
    }
  }
}));

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .email('Email must be a valid email')
    .required('Email is required')
});

const EmailCapture = ({ onCancel }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSubscribe, setIsSubscribe] = React.useState(false);
  const [notification, setNotification] = React.useState({
    open: false,
    type: '',
    message: ''
  });
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));

  const { handleSubmit, control, errors, reset } = useForm({
    validationSchema,
    defaultValues: {
      email: ''
    }
  });

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setNotification(false);
  };

  const onSubmit = ({ email }) => {
    setIsLoading(true);

    subscribeEmail(email)
      .then(result => {
        setNotification(true);

        if (result.success) {
          setIsSubscribe(true);
          return setNotification({
            open: true,
            type: 'success',
            message: 'Thanks for Subscribing!'
          });
        }
        return setNotification({
          open: true,
          type: 'error',
          message: result.message
        });
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        reset();
        setIsLoading(false);
      });
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (isSubscribe) {
        return onCancel();
      }
      return null;
    }, 3000);
    return () => clearTimeout(timer);
  }, [isSubscribe, onCancel]);

  return (
    <div className={classes.root}>
      <Container maxWidth="xl">
        <div className={classes.wrap}>
          {notification.open && (
            <Notification
              open={notification.open}
              onClose={handleClose}
              type={notification.type}
              message={notification.message}
            />
          )}

          {/* Header block */}
          <div className={classes.header}>
            <Hidden smDown>
              <div className={classes.headerIcon}>
                {/* svg for EmailIcon is not correct */}
                {/* {isSubscribe ? <EmailIcon /> : <PercentageIcon />} */}
                {isSubscribe ? (
                  <i className={classes.icon} />
                ) : (
                  <PercentageIcon />
                )}
              </div>
            </Hidden>
            <div>
              <Typography variant="h4" color="secondary">
                {t('emailCapture.title')}
              </Typography>
              <Typography variant="body2" color="secondary">
                {t('emailCapture.text')}
              </Typography>
            </div>
          </div>

          {/* Content block */}
          {isSubscribe ? (
            <div className={classes.gratitudeWrap}>
              <DoneIcon />
              <Typography variant="h4" color="secondary">
                {t('emailCapture.thankYou')}
              </Typography>
            </div>
          ) : (
            <div className={classes.formWrap}>
              {isLoading && (
                <Loader className={classes.loader} message="Sending ..." />
              )}
              <form
                className={classes.form}
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                autoComplete="off"
              >
                <FormController
                  as={TextField}
                  control={control}
                  defaultValue=""
                  type="email"
                  name="email"
                  placeholder={t('emailCapture.input')}
                  className={classes.input}
                  disabled={isLoading}
                  error={!!errors.email}
                  helperText={errors.email ? errors.email.message : ''}
                  FormHelperTextProps={{
                    classes: {
                      root: classes.helper
                    }
                  }}
                />
                <Box
                  display="flex"
                  justifyContent={matches ? 'flex-end' : 'flex-start'}
                  alignItems="center"
                >
                  <Button
                    type="button"
                    variant="contained"
                    className={classes.cancelButton}
                    disabled={isLoading}
                    onClick={onCancel}
                  >
                    {t('emailCapture.cancelButton')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isLoading}
                  >
                    {t('emailCapture.submitButton')}
                  </Button>
                </Box>
              </form>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

EmailCapture.propTypes = {
  onCancel: PropTypes.func.isRequired
};

export default EmailCapture;
